import { supabase } from './supabase'
import { functionError } from './errors'
import type { AttachmentInput } from './photos'
// A post that goes live can earn stars (099); the pill looks (REC-57).
import { nudgeStars } from './starPulse'

/**
 * The company feed and Wiki One, read from the database.
 *
 * This file replaces the post and wiki halves of lib/placeholder.ts. What is
 * here is real: every field comes out of `content` + `content_variant` through
 * the migration 012 RPCs, under RLS.
 *
 * ── WHY RPC AND NOT .from('content').select(...) ────────────────────────────
 *
 * Rule 6: content is a parent row plus locale variants, and which variant you
 * get is app.resolve_variant()'s six-branch fallback chain. supabase-js has no
 * LATERAL, so doing this as a table select means pulling every variant of every
 * post and re-deriving that chain in TypeScript — a second implementation of
 * the rule, in a language with no access to the enum ordering, guaranteed to
 * drift. Migration 011 said the chain must not be reimplemented per surface.
 * One RPC call, chain stays in one place.
 *
 * ── WHAT THESE TYPES DO NOT HAVE, AND WHY THAT IS THE POINT ─────────────────
 *
 * The placeholder FeedPost carried `views`, `reactions` and `comments`. All
 * three were invented and none had a table:
 *
 *   - views      never will. Rule 8, reach analytics are aggregate-only with no
 *                per-person read model. There is nothing to count.
 *   - reactions  REAL as of migration 018 (FED-3). Counts by kind, plus the
 *                viewer's own — and deliberately no reactor names. See below.
 *   - comments   REAL as of 056. The schema was 035's and has been usable by
 *                the feed since 8 Sep; only the count and the UI were missing.
 *
 * This paragraph used to say comments must stay ABSENT rather than default to
 * 0, because a zero renders as "nobody commented" while the truth was that
 * commenting did not exist. That reasoning was right and its subject is gone:
 * commenting exists, so 0 is now a fact about the post rather than a claim
 * about a feature. The count comes from the database (feed_page, 056), never
 * from counting a locally held array.
 *
 * `imageCaption` is gone for the same reason — attachments are FND-6.
 *
 * ── WHY THERE IS NO reactedBy FIELD ─────────────────────────────────────────
 *
 * Because the database will not tell us. The read policy on content_reaction
 * returns your own rows and nobody else's, and the counts arrive through a
 * security-definer aggregate that returns no identities at all. That is the
 * conservative reading of rule 8: a reaction is not a read, so the feature is
 * allowed, but handing every session the ability to compute who engages with
 * what across ~900 people is not something to grant by accident.
 *
 * If "seen by" lists are ever wanted, that is a blocker with an owner, not a
 * field somebody adds here and a policy somebody widens to match.
 */

/**
 * The reaction vocabulary, mirroring public.reaction_kind.
 *
 * Four, and the enum in the database is what makes a fifth a migration rather
 * than a typo. Nothing negative: a downvote on an internal post from a named
 * colleague is a different product with a different moderation burden.
 *
 * The labels are UI strings rather than content, so they do not go through
 * content_variant. What the buttons SAY is lib/hubCopy.ts `HOME.reactions`,
 * in English and Spanish (SHL-25); the `label` here is kept because the
 * moderation screens and the suite read it, and the suite fails if the two
 * English columns ever disagree.
 */
export const REACTIONS = [
  { kind: 'like', label: 'Noted', glyph: '👍' },
  { kind: 'celebrate', label: 'Well done', glyph: '🎉' },
  { kind: 'support', label: 'With you', glyph: '🤝' },
  { kind: 'learned', label: 'Learned something', glyph: '💡' },
] as const

export type ReactionKind = (typeof REACTIONS)[number]['kind']

/** kind → count. Absent key means nobody has picked it; never rendered as 0. */
export type ReactionCounts = Partial<Record<ReactionKind, number>>

/** One post, as the feed renders it. Mirrors public.feed_page's row type. */
export type FeedItem = {
  contentId: string
  isPinned: boolean
  publishedAt: string
  /**
   * 115. Who wrote it — used for ONE thing: offering Edit on the reader's own
   * post. feed_page has always returned it; the card does not render it, and
   * the server decides who may edit (edit_checked_post is author-only), so
   * this is a hint for which button to draw, never a permission.
   */
  authorPersonId: string | null
  authorName: string | null
  /**
   * 300. The byline the post was written under ("Communications"), or null.
   * When set, the card's heading is the byline and the author is the small
   * "posted by" line under it (David, 19 Oct: everyone sees who posted it).
   * `iHold` says the reader may edit or take it down — a hint for which
   * button to draw; edit_post and discard_post decide.
   */
  byline: PostByline | null
  title: string
  body: string
  /** '' = visible to the whole tenant. Otherwise the org units targeted. */
  audience: string[]
  /** Locale actually served, which may not be the one asked for. */
  variantLocale: string
  requestedLocale: string
  isFallback: boolean
  /**
   * 111. 'machine' means One translated this from the author's own language
   * (publish-post, at publish) and nobody has reviewed it. The card MUST say so
   * in teal and offer the original — 011's rule for a machine variant, and the
   * reason 011 created the column. 'reviewed' / 'approved' are a person's work.
   */
  translationStatus: 'machine' | 'reviewed' | 'approved' | null
  /** 'passed' | 'flagged' | 'failed_open' | 'pending'. 'blocked' never arrives. */
  moderationStatus: string
  /** Set by trigger when a published variant is edited. Corrections are visible. */
  editedAt: string | null
  /** Counts by kind. Aggregate only — the database returns no reactor names. */
  reactions: ReactionCounts
  /** This viewer's own reaction, or null. The one identity we are allowed. */
  myReaction: ReactionKind | null
  /** Photos, in the order the author arranged them. */
  attachments: FeedAttachment[]
  /** Free-text hashtags. `slug` is the key, `label` is what to show. */
  tags: { slug: string; label: string }[]
  /**
   * 030. A moderator took this out of the feed after publication.
   *
   * Only ever non-null for someone holding content.moderate who asked for
   * hidden rows — everyone else's RLS removes the row entirely, so this is not
   * a field to gate rendering on. It exists so an armed moderator's card can
   * SAY it is hidden. A hidden post that looks identical to a live one is how
   * somebody restores the wrong thing.
   */
  hiddenAt: string | null
  hiddenReason: string | null
  /**
   * How many replies this post has, from the database (056).
   *
   * Retracted ones are excluded — comment_count() filters deleted_at, while
   * comments_for() RETURNS a retracted row with a null body so the thread does
   * not reshape around a gap. So the count and the visible rows can differ by
   * design, and the count is the honest one: it says how many replies there
   * are, not how many tombstones.
   */
  commentCount: number
}

/**
 * One photo on a post.
 *
 * `path` and not a URL: the bucket is private, so a URL has to be signed at
 * render time by a session that passed the Storage policy. Storing a URL would
 * bake in the project ref, the bucket and the signing scheme, and all three
 * change.
 *
 * `width` and `height` are here so a card can reserve the right box BEFORE the
 * image loads. Without them the feed reflows under the reader's thumb as each
 * photo arrives, which on a phone means tapping the wrong thing.
 */
export type FeedAttachment = {
  id: string
  bucket: string
  path: string
  mime: string
  width: number
  height: number
  alt: string | null
}

/**
 * A draft's attachment, which carries one field the feed's does not.
 *
 * `bytes` exists ONLY here, and the split is deliberate rather than untidy.
 * `content_attachment.byte_size` is NOT NULL, and `edit_post` is a REPLACE that
 * takes the whole attachment set back — so a draft loaded into the composer has
 * to be able to hand its existing photos to the server again, byte_size and
 * all. `feed_page` has no reason to carry it and does not; making `bytes`
 * required on FeedAttachment would put a field on thirty feed rows to serve one
 * form, and making it optional would let the composer send `undefined` into a
 * NOT NULL column and find out at runtime.
 *
 * Migration 094 part E is the other half: `my_unpublished_posts` had to start
 * returning it. Found by writing the composer, which is the only place the read
 * and the write are ever held at once.
 */
export type DraftAttachment = FeedAttachment & { bytes: number }

export type WikiItem = {
  contentId: string
  question: string
  answeredBy: string | null
  lastReviewedAt: string | null
  isStarred: boolean
  /** 112. 'machine' when One translated the question on this row. */
  translationStatus: 'machine' | 'reviewed' | 'approved' | null
}

type FeedRow = {
  content_id: string
  is_pinned: boolean
  published_at: string
  author_person_id: string | null
  author_name: string | null
  title: string
  body: string
  audience: string[] | null
  variant_locale: string
  requested_locale: string
  is_fallback: boolean
  translation_status: 'machine' | 'reviewed' | 'approved' | null
  moderation_status: string
  edited_at: string | null
  reactions: ReactionCounts | null
  my_reaction: ReactionKind | null
  attachments: FeedAttachment[] | null
  tags: { slug: string; label: string }[] | null
  hidden_at: string | null
  hidden_reason: string | null
  comment_count: number | null
}

type WikiRow = {
  content_id: string
  question: string
  answered_by: string | null
  last_reviewed_at: string | null
  is_starred: boolean
  translation_status?: 'machine' | 'reviewed' | 'approved' | null
}

/**
 * Load the feed.
 *
 * `locale` is optional and normally omitted — the function defaults to the
 * viewer's own person.locale, which is the right answer and one fewer thing for
 * the client to get wrong. Pass it only to preview another locale.
 */
/**
 * `includeHidden` is a REQUEST, not a permission, and the distinction is the
 * whole design. Passing true does not grant anything: RLS decides whether a
 * hidden post is readable, so an ordinary employee calling this with true gets
 * exactly the rows they got with false. The client says what it wants to see;
 * the database says what may be seen.
 *
 * Which is why it is safe for the moderator-mode toggle to pass it straight
 * through from a boolean in React state. If it were a permission, that boolean
 * would be an authorisation decision living in the browser.
 */
export async function loadFeed(locale?: string, includeHidden = false): Promise<FeedItem[]> {
  const { data, error } = await supabase.rpc('feed_page', {
    p_locale: locale ?? null,
    p_limit: 30,
    p_include_hidden: includeHidden,
  })
  if (error) throw error

  return withBylines(((data ?? []) as FeedRow[]).map(toFeedItem), locale)
}

/* ── 300: BYLINES ─────────────────────────────────────────────────────────────
   feed_page and post_by_id cannot grow a column without a top-level DROP, so
   the bylines come from post_bylines() for the ids already on screen — one
   call per page, and only the posts the reader can see. */

export type PostByline = {
  key: string
  /** In the reader's language, falling back to English. */
  name: string
  /** A path in post-media, or null: the card then shows the name's initials. */
  mark: string | null
  /** The reader holds this byline, so may edit or take the post down. */
  iHold: boolean
}

type BylineRow = {
  content_id: string
  byline_key: string
  byline_name: string
  byline_mark: string | null
  i_hold: boolean
}

/** Bylines by content id. Never throws: a failed read leaves the cards
 *  showing their authors, which is true, rather than no feed at all. */
export async function loadPostBylines(ids: string[], locale?: string): Promise<Map<string, PostByline>> {
  const out = new Map<string, PostByline>()
  const unique = [...new Set(ids.filter(Boolean))]
  if (unique.length === 0) return out
  try {
    const { data, error } = await supabase.rpc('post_bylines', {
      p_content_ids: unique.slice(0, 200),
      p_locale: locale ?? null,
    })
    if (error) return out
    for (const r of (data ?? []) as BylineRow[]) {
      out.set(r.content_id, { key: r.byline_key, name: r.byline_name, mark: r.byline_mark, iHold: r.i_hold })
    }
  } catch {
    /* the authors stand */
  }
  return out
}

async function withBylines(items: FeedItem[], locale?: string): Promise<FeedItem[]> {
  const by = await loadPostBylines(items.map((i) => i.contentId), locale)
  if (by.size === 0) return items
  return items.map((i) => (by.has(i.contentId) ? { ...i, byline: by.get(i.contentId)! } : i))
}

/** 300. A byline the reader may post under (my_bylines). */
export type MyByline = { key: string; name: string; mark: string | null; tenantId: string }

/** Empty for nearly everyone, and then the composer shows nothing new. */
export async function loadMyBylines(locale?: string): Promise<MyByline[]> {
  const { data, error } = await supabase.rpc('my_bylines', { p_locale: locale ?? null })
  if (error) throw error
  return ((data ?? []) as Array<{ byline_key: string; byline_name: string; byline_mark: string | null; tenant_id: string }>).map(
    (r) => ({ key: r.byline_key, name: r.byline_name, mark: r.byline_mark, tenantId: r.tenant_id }),
  )
}

/** One feed row into the card's shape. Shared by feed_page and post_by_id, whose
 *  return types 125's suite asserts are identical. */
function toFeedItem(r: FeedRow): FeedItem {
  return {
    contentId: r.content_id,
    isPinned: r.is_pinned,
    publishedAt: r.published_at,
    authorPersonId: r.author_person_id ?? null,
    authorName: r.author_name,
    byline: null,
    title: r.title,
    body: r.body,
    audience: r.audience ?? [],
    variantLocale: r.variant_locale,
    requestedLocale: r.requested_locale,
    isFallback: r.is_fallback,
    translationStatus: r.translation_status ?? null,
    moderationStatus: r.moderation_status,
    editedAt: r.edited_at,
    reactions: r.reactions ?? {},
    myReaction: r.my_reaction,
    attachments: r.attachments ?? [],
    tags: r.tags ?? [],
    hiddenAt: r.hidden_at ?? null,
    hiddenReason: r.hidden_reason ?? null,
    commentCount: r.comment_count ?? 0,
  }
}

/**
 * 125 (HLP-23). One post, by id, in the feed's exact shape — for the #/item/:id
 * permalink a bell row opens. `null` means the reader cannot see it (removed,
 * hidden, another tenant's), and the route says so rather than guessing which.
 */
export async function loadPostById(contentId: string, locale?: string): Promise<FeedItem | null> {
  const { data, error } = await supabase.rpc('post_by_id', {
    p_content_id: contentId,
    p_locale: locale ?? null,
  })
  if (error) throw error
  const row = ((data ?? []) as FeedRow[])[0]
  if (!row) return null
  const [item] = await withBylines([toFeedItem(row)], locale)
  return item
}

/**
 * 125. What a piece of content IS, as far as the reader may know. RLS on
 * `content` is `app.content_is_visible(id)`, so a row the reader cannot see
 * comes back as nothing — the same answer as a row that does not exist, which
 * is the right one to give them.
 */
export async function contentTypeOf(contentId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('content')
    .select('content_type')
    .eq('id', contentId)
    .maybeSingle()
  if (error) throw error
  return (data?.content_type as string | undefined) ?? null
}

/**
 * The caller's own posts that are not out there — FED-22.
 *
 * Two kinds in one list, because they are one question: a plain DRAFT you saved
 * and never sent, and a post the check BLOCKED. Before 056 neither had a
 * surface, so "Save as draft" wrote into a state with no exit and a blocked
 * post vanished with its reason.
 *
 * `blockReason` is null for a draft and set for a block. That is the whole
 * difference and the client says different words for each; it is not two
 * queries, because the database answers "what of mine is unpublished" once.
 */
export type UnpublishedPost = {
  contentId: string
  createdAt: string
  updatedAt: string
  moderationStatus: string
  title: string
  body: string
  variantLocale: string
  attachments: DraftAttachment[]
  tags: { slug: string; label: string }[]
  blockReason: string | null
  blockRules: string[]
  /**
   * 041's rule, carried so the button can say the right word BEFORE the click.
   * Null means nobody ever saw it, so discarding is a real delete. THE TEST IS
   * THIS FIELD, NEVER `state` — after 040 a row can sit at draft having been
   * published for a fortnight, and reading state by eye went wrong on 8 Sep.
   */
  firstPublishedAt: string | null
}

export async function loadMyUnpublished(): Promise<UnpublishedPost[]> {
  const { data, error } = await supabase.rpc('my_unpublished_posts', {})
  if (error) throw error
  return (data ?? []).map(toUnpublished)
}

/**
 * ONE of your own LIVE posts, shaped like a draft so the one composer can load
 * it. 115's `my_post_to_edit`: the SOURCE-language words you wrote (never One's
 * translation of them) and each photo WITH its size, because the save is a
 * replace and byte_size is NOT NULL (094 E). Null when it is not yours, not
 * live, or gone — the Edit button then says it could not open it.
 */
export async function loadMyPostToEdit(contentId: string): Promise<UnpublishedPost | null> {
  const { data, error } = await supabase.rpc('my_post_to_edit', { p_content_id: contentId })
  if (error) throw error
  const row = (data ?? [])[0]
  return row ? toUnpublished(row) : null
}

function toUnpublished(r: Record<string, never>): UnpublishedPost {
  return {
    contentId: r.content_id,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    moderationStatus: r.moderation_status,
    title: r.title ?? '',
    body: r.body ?? '',
    variantLocale: r.variant_locale,
    attachments: r.attachments ?? [],
    tags: r.tags ?? [],
    blockReason: r.block_reason ?? null,
    blockRules: r.block_rules ?? [],
    firstPublishedAt: r.first_published_at ?? null,
  }
}

/**
 * Send a saved draft out, THROUGH the check.
 *
 * Deliberately the same Edge Function the composer calls, with a content_id
 * instead of a body. A second endpoint would be a second place to keep the
 * fail-open rule, the block wording and the policy version correct, and 029's
 * badge-and-bell is the worked example of two surfaces answering one question
 * and drifting apart.
 *
 * The TEXT IS NOT SENT. publish-post reads it from the row, so a caller cannot
 * submit innocuous text for grading and publish something else.
 */
export async function publishDraft(contentId: string): Promise<PublishResult> {
  const { data, error } = await supabase.functions.invoke('publish-post', {
    body: { content_id: contentId, publish: true },
  })
  if (error) throw await functionError(error)

  const res = data as Record<string, unknown>
  if (res?.blocked === true) {
    return {
      blocked: true,
      rulesHit: (res.rules_hit as string[]) ?? [],
      reason: (res.reason as string) ?? '',
      policyVersion: (res.policy_version as number) ?? null,
    }
  }
  if (res?.error) throw new Error(String(res.error))
  nudgeStars()
  // 302: a draft's photos are read when it goes live, like a new post's.
  void describePostPhotos(contentId)
  return {
    blocked: false,
    contentId: String(res.content_id),
    verdict: String(res.verdict),
    unchecked: res.unchecked === true,
  }
}

/**
 * Throw away one of your own posts.
 *
 * Returns 'deleted' when the row was really deleted (it never published, so
 * there is nothing anybody saw to preserve — 041) and 'removed' when it was
 * soft-deleted. The DATABASE decides which, from first_published_at; the
 * client only renders the word. Do not re-derive it here.
 */
export async function discardPost(contentId: string, reason?: string): Promise<string> {
  const { data, error } = await supabase.rpc('discard_post', {
    p_content_id: contentId,
    p_reason: reason?.trim() || null,
  })
  if (error) throw error
  return String(data)
}

/**
 * Publish a post, or save it as a draft.
 *
 * ── WHY AN RPC AND NOT .from('content').insert() ────────────────────────────
 *
 * Two reasons, and the second one is not obvious.
 *
 * 1. Rule 6 makes a post TWO rows — the parent and its locale variant — and
 *    supabase-js cannot write two rows in one transaction. A parent that
 *    lands without its variant is invisible to everyone including its author,
 *    while reporting itself as published. The author sees nothing, files no
 *    bug, and concludes the hub ate their post.
 *
 * 2. `.insert(...).select()` CANNOT WORK on `content`, ever. Chaining .select()
 *    makes supabase-js append RETURNING, RETURNING makes the statement a read,
 *    and the read runs content_read — whose app.content_is_visible(id) queries
 *    the table for a row its own command has not committed. It finds nothing,
 *    returns false, and Postgres rejects the insert with an error naming a
 *    WITH CHECK failure that did not happen. Proven 31 Aug; the detail is in
 *    migration 018's header.
 *
 * Returns the new content id.
 */
export async function createPost(opts: {
  body: string
  title?: string
  publish?: boolean
  attachments?: AttachmentInput[]
  tags?: string[]
  /** 300. Post under this byline key; the server refuses a non-holder. */
  byline?: string | null
}): Promise<string> {
  const { data, error } = await supabase.rpc(opts.byline ? 'create_byline_post' : 'create_post', {
    ...(opts.byline ? { p_byline: opts.byline } : {}),
    p_body: opts.body,
    p_title: opts.title?.trim() || null,
    // Omitted on purpose: p_locale defaults server-side to the author's own
    // person.locale. A crew member in Chile writes Spanish, and that has to be
    // recorded as the SOURCE locale or resolve_variant() serves the Spanish
    // text to everyone as though it were the English original.
    p_publish: opts.publish ?? true,
    p_attachments: opts.attachments ?? [],
    p_tags: opts.tags ?? [],
  })
  if (error) throw error
  // 300: a byline post earns nothing, so there is nothing to nudge.
  if ((opts.publish ?? true) && !opts.byline) nudgeStars()
  return data as string
}

/**
 * Change a post that already exists. FED-35, migration 094.
 *
 * ── A REPLACE, SO EVERY ARGUMENT IS REQUIRED (rule 9) ───────────────────────
 *
 * `set_employment`, `edit_notice`, `edit_stock_room` and `edit_shop_item` all
 * learned this the same way: with optional arguments, "remove the headline"
 * and "leave the headline alone" are the same call, and the silent outcome is
 * losing what somebody wrote. The caller sends the whole post back, including
 * the parts it is not changing. `createPost` above takes optionals because a
 * create has nothing to lose.
 *
 * ── WHAT IT RETURNS IS NOT DECORATION ───────────────────────────────────────
 *
 * `needsRecheck` is true when the post was already PUBLISHED. Editing a live
 * post moves it out of `passed` and into `failed_open` server-side, because
 * the words `passed` described are not the words that are there now — the
 * guidelines check runs at publication and nothing looked at the row again.
 * The caller is expected to SAY SO rather than swallow it: the card will carry
 * "Published unchecked" and finding that out from the feed is worse than being
 * told, which is the same rule the composer follows for its own unchecked
 * case.
 *
 * Editing a FLAGGED post deliberately leaves it flagged. A flag is a
 * moderator's open question about somebody's words, and letting the author
 * dissolve it by touching a comma would be the author overruling the
 * moderator. The server refuses that transition; this comment is here so a
 * future caller does not try to "fix" the status it gets back.
 */
export type EditResult = {
  state: string
  moderationStatus: string
  needsRecheck: boolean
}

export async function editPost(opts: {
  contentId: string
  body: string
  title: string
  attachments: AttachmentInput[]
  tags: string[]
}): Promise<EditResult> {
  const { data, error } = await supabase.rpc('edit_post', {
    p_content_id: opts.contentId,
    p_title: opts.title.trim() || null,
    p_body: opts.body,
    p_attachments: opts.attachments,
    p_tags: opts.tags,
  })
  if (error) throw error

  const row = (data as { state: string; moderation_status: string; needs_recheck: boolean }[] | null)?.[0]
  if (!row) throw new Error('the edit did not come back')

  /* 111. Changing a live post's words deletes its machine translations
     (the trigger), because they now say what the post used to say. Ask for
     new ones. Fire and forget: the edit has succeeded whatever happens here,
     and until the translation lands a reader in another language gets the
     author's own words, flagged "Shown in …" — the honest fallback. The call
     is idempotent server-side, so an edit that changed nothing costs nothing. */
  if (row.state === 'published') {
    void requestTranslation(opts.contentId)
  }

  return {
    state: row.state,
    moderationStatus: row.moderation_status,
    needsRecheck: row.needs_recheck,
  }
}

/**
 * What the moderation check said. Mirrors the publish-post Edge Function.
 *
 * `blocked` is the only case where no post exists. Everything else published —
 * including `failed_open`, which means the check could not run and the post
 * went out unscreened.
 */
export type PublishResult =
  | { blocked: true; rulesHit: string[]; reason: string; policyVersion: number | null }
  | { blocked: false; contentId: string; verdict: string; unchecked: boolean }

/**
 * Publish a post THROUGH the guidelines check (FED-10).
 *
 * ── WHY THIS AND NOT createPost ─────────────────────────────────────────────
 *
 * B09 made moderation machine-first, which means a blocked post must never
 * exist — not exist-and-then-be-hidden. A row that publishes and is deleted a
 * second later has already gone out over the notification outbox and sat in the
 * feed of whoever was looking.
 *
 * So the composer calls the Edge Function, which grades the post and only then
 * calls create_post. createPost above is still exported and is still used for
 * drafts, which are not in front of anyone and so are not checked.
 *
 * The function runs as the CALLER — it builds its Supabase client from this
 * session's token — so nothing here is a privilege escalation. Every policy
 * from 015 applies exactly as it would to a direct call.
 *
 * ── THE ERROR PATH IS NOT A BLOCK ───────────────────────────────────────────
 *
 * A network failure reaching the Edge Function throws. A BLOCK returns
 * successfully with blocked:true. Those are different things and the composer
 * shows different words for them: one says try again, the other says which rule
 * and offers an appeal. Collapsing them into one error would tell somebody
 * their connection failed when they were actually stopped.
 */
export async function publishPost(opts: {
  body: string
  title?: string
  attachments?: AttachmentInput[]
  tags?: string[]
  /** 300. Post under this byline key (publish-post v27). */
  byline?: string | null
}): Promise<PublishResult> {
  const { data, error } = await supabase.functions.invoke('publish-post', {
    body: {
      body: opts.body,
      title: opts.title?.trim() || null,
      publish: true,
      attachments: opts.attachments ?? [],
      tags: opts.tags ?? [],
      ...(opts.byline ? { byline: opts.byline } : {}),
    },
  })
  if (error) throw await functionError(error)

  const res = data as Record<string, unknown>
  if (res?.blocked === true) {
    return {
      blocked: true,
      rulesHit: (res.rules_hit as string[]) ?? [],
      reason: (res.reason as string) ?? '',
      policyVersion: (res.policy_version as number) ?? null,
    }
  }
  if (res?.error) throw new Error(String(res.error))
  if (!opts.byline) nudgeStars()
  if ((opts.attachments ?? []).length > 0 && res.content_id) void describePostPhotos(String(res.content_id))

  return {
    blocked: false,
    contentId: String(res.content_id),
    verdict: String(res.verdict ?? 'pass'),
    unchecked: res.unchecked === true,
  }
}

/**
 * Change YOUR OWN LIVE post, through the guidelines check FIRST. FED-39, 115.
 *
 * David, 25 Sep: the new words are graded before they replace the old ones.
 * pass → live and checked; flag → live, a moderator looks; BLOCK → refused and
 * nothing changes (the old words stay up, and `blocked` comes back exactly as
 * it does for a new post); check unreachable → live and marked unchecked.
 * A post a moderator has flagged stays flagged whatever the check says — the
 * server decides that, and `moderation` is what it decided.
 *
 * A REPLACE (rule 9): the whole post goes back, including the photos being
 * kept. No new photos — edit_post refuses one on a live post, because the
 * check graded the post with the photos it had.
 *
 * Not `editPost`, which stays the DRAFT edit (a draft is checked when it is
 * published). Translations are re-requested server-side, so nothing here
 * calls requestTranslation.
 */
export async function editLivePost(opts: {
  contentId: string
  body: string
  title: string
  attachments: AttachmentInput[]
  tags: string[]
}): Promise<PublishResult> {
  const { data, error } = await supabase.functions.invoke('publish-post', {
    body: {
      kind: 'edit',
      content_id: opts.contentId,
      body: opts.body,
      title: opts.title.trim() || null,
      attachments: opts.attachments,
      tags: opts.tags,
    },
  })
  if (error) throw await functionError(error)

  const res = data as Record<string, unknown>
  if (res?.blocked === true) {
    return {
      blocked: true,
      rulesHit: (res.rules_hit as string[]) ?? [],
      reason: (res.reason as string) ?? '',
      policyVersion: (res.policy_version as number) ?? null,
    }
  }
  if (res?.error) throw new Error(String(res.error))
  // 302: an edit may add photos (144); One reads any it has not read yet.
  if (opts.attachments.length > 0) void describePostPhotos(opts.contentId)

  return {
    blocked: false,
    contentId: String(res.content_id),
    verdict: String(res.verdict ?? 'pass'),
    unchecked: res.unchecked === true,
  }
}

/**
 * Set, change or clear this viewer's reaction, in one call.
 *
 * Passing the kind already selected clears it, because that is what pressing a
 * pressed button means. Returns the post's fresh counts and the viewer's own
 * reaction, so the caller never has to guess what the server did — which
 * matters because the toggle-to-clear rule lives in the function, not here.
 */
export async function reactToContent(
  contentId: string,
  kind: ReactionKind | null,
): Promise<{ counts: ReactionCounts; mine: ReactionKind | null }> {
  const { data, error } = await supabase.rpc('react_to_content', {
    p_content_id: contentId,
    p_kind: kind,
  })
  if (error) throw error
  const res = (data ?? {}) as { counts?: ReactionCounts; mine?: ReactionKind | null }
  return { counts: res.counts ?? {}, mine: res.mine ?? null }
}

/**
 * The same, for a reply (058).
 *
 * A separate exported function mirroring a separate RPC, rather than one
 * `reactTo(target)` taking a discriminated union. 058's header has the database
 * half of the argument (030's rule: a function whose first argument means two
 * different things is how the wrong row gets written); the client half is that
 * the call site reads `reactToComment(c.commentId, kind)` instead of
 * `reactTo({ commentId: c.commentId }, kind)`, and there is nothing to get
 * wrong in the first form.
 *
 * Note this is NOT the shape castVote uses — that one takes
 * `{ contentId?, commentId? }` because it was written before there was a second
 * caller to learn from. Both work; if they are ever unified, unify them
 * towards this one.
 */
export async function reactToComment(
  commentId: string,
  kind: ReactionKind | null,
): Promise<{ counts: ReactionCounts; mine: ReactionKind | null }> {
  const { data, error } = await supabase.rpc('react_to_comment', {
    p_comment_id: commentId,
    p_kind: kind,
  })
  if (error) throw error
  const res = (data ?? {}) as { counts?: ReactionCounts; mine?: ReactionKind | null }
  return { counts: res.counts ?? {}, mine: res.mine ?? null }
}

/** Total across every kind. Used for the "N reactions" summary line. */
export function totalReactions(counts: ReactionCounts): number {
  return Object.values(counts).reduce<number>((a, b) => a + (b ?? 0), 0)
}

/**
 * The rail's Wiki One card: what people are actually reading. SHL-16.
 *
 * ── TRENDING, NOT RECENT, AND FOUR RATHER THAN SIX ──────────────────────────
 *
 * `wiki_answers_recent` ordered by when an answer was written, which is a fact
 * about the Wiki One team's week rather than about the reader's. On a corpus
 * that is 26 Brand Change FAQ entries loaded in one afternoon, "recent" is
 * very nearly arbitrary — they all arrived together.
 *
 * `wiki_search` has taken `p_sort => 'trending'` since 038, so this is the
 * function that already existed being asked the better question. Four, because
 * the rail card sits above three other cards and SHL-18's 375px arithmetic has
 * no slack in it.
 *
 * ── THE EMPTY QUERY IS THE POINT, NOT A HACK ────────────────────────────────
 *
 * `p_query => ''` means "not filtered by words", and the sort then decides the
 * order — the same call `#/wiki` makes when nobody has typed anything. The
 * limit is applied BY THE DATABASE (rule 1), so this is four of the trending
 * answers and not four of everything sorted afterwards in the browser.
 *
 * `p_state` is left at its default of 'published'. One answers from published
 * content only and so does this card; an author's own drafts come back from
 * `content_is_visible` and David authored all 26 FAQ entries, so a widened
 * state here would show him a rail nobody else can see.
 */
export async function loadWikiAnswers(locale?: string): Promise<WikiItem[]> {
  const { data, error } = await supabase.rpc('wiki_search', {
    p_query: '',
    p_locale: locale ?? null,
    p_limit: 4,
    p_sort: 'trending',
  })
  if (error) throw error

  return ((data ?? []) as WikiRow[]).map((r) => ({
    contentId: r.content_id,
    question: r.question,
    answeredBy: r.answered_by,
    lastReviewedAt: r.last_reviewed_at,
    isStarred: r.is_starred,
    translationStatus: r.translation_status ?? null,
  }))
}

/**
 * What the company is talking about — tags across posts AND replies (060).
 *
 * `uses` is rows, not distinct people: one person tagging the same thing on ten
 * replies counts ten times. That is the honest reading of "being talked about",
 * and the alternative — distinct people — is a per-person measure over content,
 * which is the shape rule 8 refuses.
 */
export type TrendingTag = {
  slug: string
  label: string
  uses: number
  posts: number
  replies: number
}

/**
 * SECURITY INVOKER on the server, so RLS decides what is countable and a
 * targeted post's tags stay uncountable by somebody outside its audience. Two
 * readers seeing different lists is correct, not a caching bug.
 */
export async function loadTrendingTags(days = 7, limit = 8): Promise<TrendingTag[]> {
  const { data, error } = await supabase.rpc('trending_tags', {
    p_days: days,
    p_limit: limit,
  })
  if (error) throw error
  return ((data ?? []) as Record<string, never>[]).map((r) => ({
    slug: r.slug,
    label: r.label,
    uses: r.uses ?? 0,
    posts: r.posts ?? 0,
    replies: r.replies ?? 0,
  }))
}

/** Label for a post with no content_audience rows — visible to the whole tenant. */
export const EVERYONE = 'Everyone'

/**
 * Split the feed into the pinned hero and the list, and count the audiences.
 *
 * This lived inline in routes/Home.tsx as three useMemos. It moved here for one
 * reason: it was the only logic on the page that could be wrong in a way that
 * still compiled and still rendered — an off-by-one in the pinned split shows
 * as a post silently missing from the feed, which is invisible until someone
 * asks why their post never appeared. Rendering the components proves the
 * markup; only this proves the arithmetic.
 *
 * The pinned row is REMOVED from the list and returned separately, but only the
 * first one. A second pinned post stays in the list rather than vanishing —
 * dropping every pinned row would mean pinning two posts published one of them.
 */
export function shapeFeed(posts: FeedItem[]): {
  pinned: FeedItem | null
  rest: FeedItem[]
  counts: Map<string, number>
} {
  const i = posts.findIndex((p) => p.isPinned)
  const pinned = i === -1 ? null : posts[i]
  const rest = i === -1 ? posts : posts.filter((_, n) => n !== i)

  const counts = new Map<string, number>()
  for (const p of rest) {
    for (const label of p.audience.length === 0 ? [EVERYONE] : p.audience) {
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
  }
  return { pinned, rest, counts }
}

/**
 * WHERE THE RECOGNITION GROUPS GO — 333 (30 Oct 2026).
 *
 * The feed is posts (feed_page, untouched). Recognitions given between two
 * posts are one Star One card in that gap, read from feed_recognition_groups.
 * This is the arithmetic that says which gap goes before which post, and it is
 * here rather than in Home for shapeFeed's reason: it can be wrong while still
 * compiling and still rendering, so it is unit-tested.
 *
 * `bounds` are the publish times of the NON-pinned posts, in the order shown,
 * which feed_page makes newest first. `gapAt[i]` is the gap to render before
 * `rest[i]` (null for a pinned post that stayed in the list, which sits out of
 * time order and owns no gap). A pinned post is not a bound: it is old news
 * kept at the top, and recognitions given since it was posted are not "between"
 * anything because of it.
 *
 * If the times are ever NOT newest first, there are no bounds at all. The
 * server refuses an unordered list (rule 12), and a card in the wrong gap
 * would be a quiet lie about when people were thanked; no card is the honest
 * failure.
 */
export function placeRecognitionGroups(rest: FeedItem[]): { bounds: string[]; gapAt: (number | null)[] } {
  const bounds: string[] = []
  const gapAt: (number | null)[] = []
  for (const p of rest) {
    if (p.isPinned) {
      gapAt.push(null)
      continue
    }
    gapAt.push(bounds.length)
    bounds.push(p.publishedAt)
  }
  for (let i = 1; i < bounds.length; i++) {
    if (new Date(bounds[i]).getTime() > new Date(bounds[i - 1]).getTime()) {
      return { bounds: [], gapAt: rest.map(() => null) }
    }
  }
  return { bounds: bounds.slice(0, 31), gapAt: gapAt.map((g) => (g !== null && g < 31 ? g : null)) }
}

/**
 * Apply the audience chip.
 *
 * EVERYONE is not an audience the database knows about — it is the ABSENCE of
 * content_audience rows. So it cannot be matched with includes(), and writing
 * it that way returns an empty list for the most common filter on the page.
 */
export function filterByAudience(posts: FeedItem[], audience: string | null): FeedItem[] {
  if (audience === null) return posts
  if (audience === EVERYONE) return posts.filter((p) => p.audience.length === 0)
  return posts.filter((p) => p.audience.includes(audience))
}

/**
 * "2 days ago" from a timestamp.
 *
 * Rule 9 is store UTC plus an explicit site timezone, and this deliberately
 * does neither — a relative label needs no timezone at all, which is exactly
 * why it is safe here and why absolute dates elsewhere must not be formatted
 * this casually. The moment a screen needs to say "9:00 on 13 October", it
 * needs the site timezone, and this function is the wrong tool.
 *
 * Intl.RelativeTimeFormat rather than a hand-rolled ladder: 'hace 2 días' for
 * es-CL and 'il y a 2 jours' for fr-FR come free, and a hand-rolled version
 * would have shipped English into both.
 */
export function relativeTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''

  const seconds = Math.round((then - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
  ]

  let value = seconds
  for (const [unit, span] of steps) {
    if (Math.abs(value) < span) return rtf.format(Math.round(value), unit)
    value /= span
  }
  return rtf.format(Math.round(value), 'year')
}

/**
 * 111. Ask publish-post to translate a live post into the languages it is
 * still missing. Never throws — a translation that does not arrive is a
 * fallback, not an error the person who edited can do anything about.
 */
export async function requestTranslation(contentId: string): Promise<string[]> {
  try {
    const { data, error } = await supabase.functions.invoke('publish-post', {
      body: { kind: 'translate', content_id: contentId },
    })
    if (error) return []
    return ((data as { translated?: string[] } | null)?.translated ?? [])
  } catch {
    return []
  }
}

/**
 * 302 (FED-70). Ask One to read the photos on a post you just put live and
 * suggest what is in them (David, 20 Oct). Never throws, and nobody waits
 * for it: the post is up whatever happens here, and a photo it misses is
 * read the next time somebody opens the Gallery. Only the post's author may
 * ask; the server lists only photos that are in the Gallery.
 */
export async function describePostPhotos(contentId: string): Promise<void> {
  try {
    await supabase.functions.invoke('photo-room', { body: { kind: 'describe_post', content_id: contentId } })
  } catch {
    /* nothing to tell anyone */
  }
}

/** The author's own words behind a translated post — "Show original" (111). */
export type PostOriginal = { title: string; body: string; locale: string }

export async function loadPostOriginal(contentId: string): Promise<PostOriginal | null> {
  const { data, error } = await supabase.rpc('post_original', { p_content_id: contentId })
  if (error) throw error
  const row = (data as { title: string | null; body: string | null; locale: string }[] | null)?.[0]
  if (!row) return null
  return { title: row.title ?? '', body: row.body ?? '', locale: row.locale }
}
